import Vue from 'vue'
import auth from './auth'
import toast from './toast'
import catalog from './catalog'
import { platform } from '@/services/platform'
import {
  MAX_TRIAL_TRACKS,
  setTrialSelection,
  toggleTrialSelection,
  validateTrialSelection
} from '@/services/trialRules.mjs'

// 本地仅缓存展示状态；是否可访问最终由产品后端查询平台权益决定。
const TRIAL_STORAGE_KEY = 'stellarcore_trial_display_records'

// Web 地址可在测试环境覆盖；SSO redirect_uri 必须与目标端兑换时完全一致。
const TRIAL_TARGET_URL = (
  process.env.VUE_APP_WEB_BASE_URL || 'https://data.stellarcore.world'
).replace(/\/$/, '')
const WEB_SSO_CLIENT_ID =
  process.env.VUE_APP_WEB_SSO_CLIENT_ID || 'stellarcore-web'

// 关键状态：全局试用弹窗、当前视图和当前账号试用记录
const trialState = Vue.observable({
  visible: false,
  view: 'select',
  selectedFeatures: [],
  currentRecord: null,
  errorMessage: '',
  checking: false
})

// 方法：从全局目录筛选可申请赛道，提交值使用标准化赛道 ID。
export function getTrialFeatureOptions() {
  return catalog.state.tracks
    .filter(track => track && track.available && track.name)
    .map(track => ({
      value: track.id,
      label: track.name
    }))
}

// 方法：从 localStorage 安全读取全部 mock 试用记录
function readTrialRecords() {
  try {
    const storedRecords = window.localStorage.getItem(TRIAL_STORAGE_KEY)
    return storedRecords ? JSON.parse(storedRecords) : {}
  } catch (error) {
    return {}
  }
}

// 方法：把全部 mock 试用记录写回 localStorage
function writeTrialRecords(records) {
  try {
    window.localStorage.setItem(TRIAL_STORAGE_KEY, JSON.stringify(records))
  } catch (error) {
    // 逻辑分支：浏览器禁用 localStorage 时，仅保留本次页面内弹窗展示态
  }
}

// 方法：获取当前 mock 登录手机号，所有试用权益都绑定手机号判断
function getCurrentMobile() {
  return auth.state.user && auth.state.user.mobile ? auth.state.user.mobile : ''
}

// 方法：读取指定手机号的试用记录
export function getTrialRecord(mobile = getCurrentMobile()) {
  if (!mobile) {
    return null
  }

  const records = readTrialRecords()
  return records[mobile] || null
}

// 方法：判断当前手机号是否存在仍在有效期内的试用记录
export function hasValidTrial(mobile = getCurrentMobile()) {
  const record = getTrialRecord(mobile)

  // 逻辑分支：没有申请记录时，不具备有效试用权益
  if (!record || !record.trialApplied) {
    return false
  }

  return Number(record.trialExpiresAt) > Date.now()
}

// 方法：统一把权益接口的数组或分页结构规整为数组
function normalizeEntitlements(entitlements) {
  return Array.isArray(entitlements)
    ? entitlements
    : (entitlements && entitlements.items) || []
}

// 方法：按 sources 字段判断权益来源
function hasEntitlementSource(entitlement, source) {
  return Boolean(
    entitlement &&
    Array.isArray(entitlement.sources) &&
    entitlement.sources.includes(source)
  )
}

// 方法：判断权益是否仍在有效期内，未返回 expires_at 时按长期有效处理
function isEntitlementActive(entitlement) {
  if (!entitlement || !entitlement.expires_at) {
    return true
  }

  return Date.parse(entitlement.expires_at) > Date.now()
}

// 方法：从试用权益里提取最晚过期时间，便于同步本地展示缓存
function latestTrialExpiresAt(trialEntitlements) {
  return trialEntitlements.reduce((latest, entitlement) => {
    const expiresAt = Date.parse(entitlement.expires_at)

    // 逻辑分支：接口未返回或返回非法时间时，不参与本地有效期比较
    if (Number.isNaN(expiresAt)) {
      return latest
    }

    return Math.max(latest, expiresAt)
  }, 0)
}

// 方法：把后端试用权益同步成本地展示记录
function syncTrialRecordFromEntitlements(mobile, trialEntitlements) {
  const records = readTrialRecords()
  const trialRecord = {
    mobile,
    trialApplied: true,
    trialFeatures: trialEntitlements
      .map(entitlement => entitlement.track_id)
      .filter(Boolean),
    trialStartedAt: records[mobile] ? records[mobile].trialStartedAt : null,
    trialExpiresAt: latestTrialExpiresAt(trialEntitlements),
    trialId: records[mobile] ? records[mobile].trialId : null
  }

  records[mobile] = trialRecord
  writeTrialRecords(records)
  trialState.currentRecord = trialRecord

  return trialRecord
}

// 方法：检查当前账号是否已申请试用、试用是否仍有效
async function queryTrialEligibility(mobile) {
  const entitlements = await platform.entitlements()
  const entitlementItems = normalizeEntitlements(entitlements)
  const trialEntitlements = entitlementItems.filter(entitlement =>
    hasEntitlementSource(entitlement, 'trial')
  )

  // 逻辑分支：权益里没有试用来源时，当前账号仍可打开试用申请弹窗
  if (trialEntitlements.length === 0) {
    return { status: 'not_applied', record: null }
  }

  const activeTrialEntitlements = trialEntitlements.filter(isEntitlementActive)
  const record = syncTrialRecordFromEntitlements(mobile, trialEntitlements)

  // 逻辑分支：存在未过期试用权益时，直接进入 Web 端
  if (activeTrialEntitlements.length > 0) {
    return { status: 'active', record }
  }

  return { status: 'expired', record }
}

// 方法：使用 60 秒、一次性 SSO code 打开 Web，避免跨域传递 Access Token。
export async function openTrialTarget() {
  const redirectUri = `${TRIAL_TARGET_URL}/auth/callback`
  const targetWindow = window.open('about:blank', '_blank')
  if (targetWindow) {
    targetWindow.opener = null
  }

  try {
    const issued = await platform.issueSsoCode(
      WEB_SSO_CLIENT_ID,
      redirectUri
    )
    const targetUrl = new URL(redirectUri)
    targetUrl.searchParams.set('code', issued.code)

    if (targetWindow) {
      targetWindow.location.href = targetUrl.toString()
    } else {
      window.location.assign(targetUrl.toString())
    }
    return true
  } catch (error) {
    if (targetWindow) {
      targetWindow.close()
    }
    trialState.errorMessage =
      error.message || '进入 Web 服务失败，请稍后重试'
    return false
  }
}

// 方法：关闭全局试用弹窗并清理本次未提交选择
export function closeTrialDialog() {
  trialState.visible = false
  trialState.view = 'select'
  trialState.selectedFeatures = []
  trialState.errorMessage = ''
}

// 方法：切换申请弹窗中的多选功能项，最多可选 MAX_TRIAL_TRACKS 个赛道
export function toggleTrialFeature(featureValue) {
  const result = toggleTrialSelection(
    trialState.selectedFeatures,
    featureValue,
    MAX_TRIAL_TRACKS
  )
  trialState.selectedFeatures = result.next
  trialState.errorMessage = result.error
}

// 方法：按复选组件传回的布尔值设置功能选中态，最多可选 MAX_TRIAL_TRACKS 个赛道
export function setTrialFeature(featureValue, checked) {
  const result = setTrialSelection(
    trialState.selectedFeatures,
    featureValue,
    checked,
    MAX_TRIAL_TRACKS
  )
  trialState.selectedFeatures = result.next
  trialState.errorMessage = result.error
}

// 方法：开始免费试用流程，先查权益资格，再按有效、过期、未申请分流
export async function startTrialFlow() {
  const mobile = getCurrentMobile()

  // 逻辑分支：理论上入口已 requireLogin，兜底避免未登录时写入空账号
  if (!mobile) {
    return
  }

  // 逻辑分支：资格查询尚未完成时忽略重复点击，避免重复打开窗口或弹窗
  if (trialState.checking) {
    return
  }

  trialState.checking = true
  trialState.errorMessage = ''

  try {
    // 关键节点：打开申请弹窗前确保赛道目录已加载，避免展示空选项或过期赛道。
    await catalog.initializeCatalog()
    const eligibility = await queryTrialEligibility(mobile)

    // 逻辑分支：已申请且未过期时，直接跳转到 Web 端
    if (eligibility.status === 'active') {
      openTrialTarget()
      return
    }

    // 逻辑分支：已申请但已过期时，只提示购买，不再打开申请弹窗
    if (eligibility.status === 'expired') {
      trialState.currentRecord = eligibility.record
      trialState.visible = false
      toast.show('试用已过期，请购买套餐')
      return
    }

    trialState.currentRecord = null
    trialState.selectedFeatures = []
    trialState.view = 'select'
    trialState.visible = true
  } catch (error) {
    const record = getTrialRecord(mobile)

    // 逻辑分支：权益接口异常时，使用本地已知试用记录兜底分流
    if (record && record.trialApplied && Number(record.trialExpiresAt) > Date.now()) {
      openTrialTarget()
      return
    }

    // 逻辑分支：本地已知已申请且过期时，按过期处理并提示购买
    if (record && record.trialApplied) {
      trialState.currentRecord = record
      trialState.visible = false
      toast.show('试用已过期，请购买套餐')
      return
    }

    toast.show(error.message || '试用资格查询失败，请稍后再试')
  } finally {
    trialState.checking = false
  }
}

// 方法：提交试用申请，并写入当前手机号的 7 天试用权益
export async function applyTrial() {
  const mobile = getCurrentMobile()

  // 逻辑分支：未选择功能或超过数量上限时阻止提交，停留在申请弹窗提示用户
  const selectionError = validateTrialSelection(
    trialState.selectedFeatures,
    MAX_TRIAL_TRACKS
  )
  if (selectionError) {
    trialState.errorMessage = selectionError
    return false
  }

  // 逻辑分支：登录态异常时阻止写入试用记录
  if (!mobile) {
    trialState.errorMessage = '请先登录后再申请试用'
    return false
  }

  try {
    // 试用 SKU 配置化：默认 mini（官网试用只开通小程序试看，
    // Web 需购买）；业务批准双端试用时设置 VUE_APP_TRIAL_PLAN_SKU=both
    const result = await platform.startTrial(
      process.env.VUE_APP_TRIAL_PLAN_SKU || 'mini',
      trialState.selectedFeatures.slice()
    )
    const records = readTrialRecords()
    const trialRecord = {
      mobile,
      trialApplied: true,
      trialFeatures: result.track_ids,
      trialStartedAt: Date.parse(result.starts_at),
      trialExpiresAt: Date.parse(result.ends_at),
      trialId: result.id
    }
    records[mobile] = trialRecord
    writeTrialRecords(records)
    trialState.currentRecord = trialRecord
    trialState.errorMessage = ''
    trialState.view = 'success'
    return true
  } catch (error) {
    if (error.code === 'TRIAL_ALREADY_CLAIMED') {
      trialState.visible = false
      toast.show('您已领取过试用，无法重复领取')
      return false
    }
    trialState.errorMessage = error.message || '试用开通失败，请稍后再试'
    return false
  }
}

export default {
  state: trialState,
  getTrialFeatureOptions,
  startTrialFlow,
  applyTrial,
  hasValidTrial,
  openTrialTarget,
  closeTrialDialog,
  toggleTrialFeature,
  setTrialFeature,
  getTrialRecord
}
